<template>
  <FilterOverview :showFilterInitially="false" title="Touristische Planung" @reload="onReload">
    <template #toolbar-left>
      <TouristischePlanungToolbarFilter @change="onFilterChange" />
    </template>

    <template #toolbar-right>
      <div class="d-flex">
        <div
          class="icon-button d-flex justify-content-center mr-4"
          style="width: 36px"
          @click="openAufgabenModalWithData"
          v-b-tooltip.hover.bottom
          :title="'Aufgabe erstellen'"
        >
          <span class="d-flex align-items-center"><ListChecks :size="20" /></span>
        </div>
      </div>
    </template>

    <template #table="{ tableHeight }">
      <AgGridVue
        :style="{ height: tableHeight + 87 + 'px' }"
        rowHeight="50px"
        class="ag-theme-alpine m-0 p-0"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowModelType="'serverSide'"
        :enableCellTextSelection="true"
        :statusBar="statusBar"
        rowSelection="multiple"
        :tooltipShowDelay="0"
        :tooltipMouseTrack="true"
        suppressCellFocus
        overlayNoRowsTemplate="Es wurde kein passender Reisetermin gefunden."
        @rowSelected="onRowSelected"
        @grid-ready="onGridReady"
        @cell-editing-stopped="onPaxBearbeiten"
      ></AgGridVue>

      <AufgabeErstellenModal
        :prefixedVerlinkungen="prefixedVerlinkungen"
        :disableAttachments="false"
        :fromFVC="false"
        :enableManualTask="true"
      />
    </template>
  </FilterOverview>
</template>

<script>
import FilterOverview from '@/components/common/filter-overview.vue';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';

import TouristischePlanungStatusBar from './touristische-planung-status-bar.vue';
import TouristischePlanungHoverTooltip from './touristische-planung-hover-tooltip.vue';
import CustomTooltip from '@/components/flugverfuegbarkeit/custom-tooltip.vue';
import TouristischePlanungToolbarFilter from './touristische-planung-toolbar-filter.vue';

import CellRendererPax from '@/components/produkte/reisetermine/cell-renderer-pax.vue';
import CustomInfoIconHeader from '@/components/common/custom-info-icon-header.vue';
import CustomIconExclamation from '@/components/common/custom-icon-exclamation.vue';

import * as odataService from '@/core/common/services/odata.service';
import apiService from '@/core/common/services/api.service';
import buildQuery from 'odata-query';

import { formatDateWithoutHours, vorzeitigGarantiertClass } from '@/core/common/helpers/utils';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import {
  buildPaxPotenzialTooltip,
  buildFcUmsatzTooltip,
  hasTouristischePlanungErgebnis,
} from './touristische-planung-tooltip-helpers';
import { ListChecks } from 'lucide-vue';
import { reiseCountryName } from '@/core/common/iso3166Countries.js';

const BLANK_ANGELEGT_STATES = [
  'AvailableInBlank',
  'AvailableInBlankAndSyncedHotelleistungWithRelations',
  'AvailableInBlankAndSyncedHotelleistungWithRelationsAndKontingente',
  'AvailableInBlankAndSyncedHotelleistungWithRelationsAndKontingenteAndZusatzleistungen',
];

// Nicht-reaktive Konstanten und reine Helper-Funktionen (kein `this` nötig) – daher über dem export default
const STATUS_BAR_KEY = 'touristischePlanungStatusBar';
const STATE_LABELS = Object.freeze({
  CmBestaetigt: 'CM bestätigt',
  CmAngefragt: 'CM angefragt',
  Durchgefuehrt: 'Durchgeführt',
  Durchfuehrung: 'Durchführung',
});

export default {
  components: {
    ListChecks,
    FilterOverview,
    AgGridVue,
    TouristischePlanungStatusBar,
    TouristischePlanungHoverTooltip,
    CustomTooltip,
    TouristischePlanungToolbarFilter,
    CellRendererPax,
    AufgabeErstellenModal,
    CustomInfoIconHeader,
    CustomIconExclamation,
  },

  data() {
    return {
      gridApi: null,
      filters: this.getEmptyFilters(),

      allSelectedRows: [],
      prefixedVerlinkungen: [],
      columnDefs: [],
      statusBar: null,

      defaultColDef: {
        flex: 1,
        filter: false,
        sortable: false,
        resizable: false,
        floatingFilter: false,
        suppressMenu: true,
        suppressMovable: true,
        tooltipComponent: 'CustomTooltip',
      },
    };
  },

  created() {
    this.statusBar = {
      statusPanels: [{ statusPanel: 'TouristischePlanungStatusBar', key: STATUS_BAR_KEY, align: 'left' }],
    };

    this.columnDefs = [
      {
        field: 'istZusatztermin',
        headerName: '',
        cellRenderer: params => (params.value ? 'Z' : ''),
        width: 50,
        minWidth: 50,
        pinned: 'left',
        cellStyle: { textAlign: 'center' },
      },
      {
        field: 'reise.region',
        headerName: 'Region',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        minWidth: 130,
        maxWidth: 130,
        pinned: 'left',
      },
      {
        field: 'reise.country',
        headerName: 'Land',
        cellRenderer: params => (params.value ? reiseCountryName(params.value) : '-'),
        minWidth: 130,
        maxWidth: 160,
        pinned: 'left',
      },
      {
        field: 'reise.reisekuerzel',
        headerName: 'Reisekürzel',
        valueFormatter: params => params.value || '-',
        minWidth: 100,
        maxWidth: 100,
        pinned: 'left',
      },
      {
        field: 'state',
        headerName: 'Status',
        valueFormatter: params => this.formatState(params.value),
        minWidth: 120,
        maxWidth: 120,
        pinned: 'left',
      },
      {
        field: 'startdatum',
        headerName: 'Startdatum',
        valueFormatter: params => formatDateWithoutHours(params.value),
        sortable: true,
        minWidth: 100,
        maxWidth: 100,
        pinned: 'left',
      },
      {
        field: 'releasedatum',
        headerName: 'Releasedatum',
        valueFormatter: params => formatDateWithoutHours(params.value),
        sortable: true,
        minWidth: 120,
      },
      {
        field: 'countryManagers',
        headerName: 'CM',
        cellRenderer: params =>
          params.value?.length ? params.value.map(cm => cm.countryManagerKuerzel).join(', ') : '-',
        tooltipValueGetter: params =>
          params.value && params.value.length > 1
            ? params.value.map(cm => cm.countryManagerKuerzel).join(', ')
            : null,
        minWidth: 100,
        maxWidth: 100,
      },
      {
        field: 'paxWidget',
        headerName: 'PAX',
        cellRenderer: 'CellRendererPax',
        minWidth: 160,
        maxWidth: 160,
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'justify-items': 'center',
          overflow: 'visible',
          'align-items': 'center',
        },
        cellRendererParams: params => ({
          paxObject: params.data?.pax,
          minPax: params.data?.metadata?.minPax,
          maxPax: params.data?.metadata?.maxPax,
          kalkPax: params.data?.metadata?.kalkPax,
          forecastPax: params.data?.metadata?.forecastPax,
          durchfuehrbareMinPax: params.data?.metadata?.durchfuehrbareMinPax,
          id: params.data?.id,
        }),
      },
      {
        field: 'umsatz',
        headerName: 'IST Umsatz',
        cellRenderer: params => this.$options.filters.currency(params.value || 0),
        cellStyle: { textAlign: 'right' },
        minWidth: 100,
        maxWidth: 100,
      },
      {
        field: 'garantiert',
        headerName: 'Gar.',
        minWidth: 50,
        maxWidth: 50,
        cellRenderer: params =>
          params.value
            ? "<i class='fas fa-check text-success fa-lg'></i>"
            : "<i class='ml-1 fas fa-times text-danger fa-lg'></i>",
      },
      {
        field: 'vorzeitigGarantiert',
        headerName: 'Vorz. gar.',
        minWidth: 80,
        maxWidth: 80,
        cellRenderer: params => {
          const iconClass = this.vorzeitigGarantiertClass(params.value);
          const tooltip = params.data?.garantiertSeit
            ? formatDateWithoutHours(params.data.garantiertSeit)
            : '';
          return `<span title="${tooltip}"><i class="${iconClass}"></i></span>`;
        },
      },
      {
        field: 'stopSale',
        headerName: 'Stop Sale',
        minWidth: 80,
        maxWidth: 80,
        cellRenderer: 'CustomIconExclamation',
        cellStyle: { alignContent: 'center' },
      },
      // Editierbare Plan-Pax- und FC-Pax-Spalten
      {
        field: 'metadata.planPax',
        headerName: 'Plan Pax',
        editable: true,
        minWidth: 80,
        maxWidth: 80,
        cellRenderer: params => (params.value != null ? params.value : '-'),
        cellStyle: { cursor: 'pointer', alignContent: 'center' },
        valueSetter: params => {
          const parsed = parseInt(String(params.newValue).trim(), 10);
          if (isNaN(parsed)) return false;
          params.data.metadata.planPax = parsed;
          return true;
        },
      },
      {
        field: 'metadata.forecastPax',
        headerName: 'FC Pax',
        editable: true,
        minWidth: 80,
        maxWidth: 80,
        cellRenderer: params => (params.value != null ? params.value : '-'),
        cellStyle: { cursor: 'pointer', alignContent: 'center' },
        valueSetter: params => {
          const parsed = parseInt(String(params.newValue).trim(), 10);
          if (isNaN(parsed)) return false;
          params.data.metadata.forecastPax = parsed;
          return true;
        },
      },
      {
        field: 'forecastPaxPotenzial',
        headerName: 'FC Pax Potenzial',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Weitere Informationen sind per Hover über die Zahlen verfügbar.',
        },
        minWidth: 160,
        cellRenderer: params => (params.value != null ? params.value : '-'),
        tooltipComponent: 'TouristischePlanungHoverTooltip',
        tooltipValueGetter: params => buildPaxPotenzialTooltip(params.data),
        cellStyle: params =>
          hasTouristischePlanungErgebnis(params.data)
            ? { cursor: 'pointer', alignContent: 'center' }
            : { alignContent: 'center' },
      },
      {
        field: 'durchschnittlicherVk',
        headerName: 'Ø VK',
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: params => this.$options.filters.currency(params.value || 0),
        cellStyle: { textAlign: 'right' },
      },
      {
        field: 'planUmsatz',
        headerName: 'Plan Umsatz',
        minWidth: 110,
        cellRenderer: params => this.$options.filters.currency(params.value || 0),
        cellStyle: { textAlign: 'right' },
      },
      {
        field: 'forecastUmsatz',
        headerName: 'FC Umsatz',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Weitere Informationen sind per Hover über die Zahlen verfügbar.',
        },
        minWidth: 130,
        cellRenderer: params => this.$options.filters.currency(params.value || 0),
        tooltipComponent: 'TouristischePlanungHoverTooltip',
        tooltipValueGetter: params => buildFcUmsatzTooltip(params.data, this.$options.filters.currency),
        cellStyle: params =>
          hasTouristischePlanungErgebnis(params.data)
            ? { cursor: 'pointer', textAlign: 'right' }
            : { textAlign: 'right' },
      },
      {
        field: 'forecastUmsatzPotenzial',
        headerName: 'FC Umsatz Potenzial',
        minWidth: 160,
        cellRenderer: params => this.$options.filters.currency(params.value || 0),
        cellStyle: { textAlign: 'right' },
      },
      {
        field: 'forecastUmsatzAtRisk',
        headerName: 'FC Umsatz at risk',
        minWidth: 140,
        cellRenderer: params => this.$options.filters.currency(params.value || 0),
        cellStyle: { textAlign: 'right' },
      },
    ];
  },

  methods: {
    vorzeitigGarantiertClass,

    formatState(state) {
      return STATE_LABELS[state] || state;
    },

    // Empfängt den neuen Filter-State vom Toolbar-Filter und triggert ein Grid-Reload
    onFilterChange(filters) {
      this.filters = filters;
      if (this.gridApi) {
        this.gridApi.onFilterChanged();
      }
    },

    onGridReady(params) {
      this.gridApi = params.api;

      const server = this.server();
      const datasource = this.createDatasource(server);
      params.api.setServerSideDatasource(datasource);
    },

    createDatasource(server) {
      return {
        getRows: async params => {
          const response = await server.getData(params.request);
          if (response.success) {
            const rows = response.rows;

            params.success({ rowData: rows });

            if (rows.length === 0) {
              this.gridApi?.showNoRowsOverlay();
            } else {
              this.gridApi?.hideOverlay();
            }

            this.updateSums();
          } else {
            params.fail();
          }
        },
      };
    },

    server() {
      return {
        getData: async request => {
          const odataFilter = this.getOdataFilter();

          const selectedFields = [
            'id',
            'reiseterminkuerzel',
            'istZusatztermin',
            'state',
            'startdatum',
            'releasedatum',
            'garantiert',
            'garantiertSeit',
            'vorzeitigGarantiert',
            'stopSale',
            'umsatz',
            'durchschnittlicherVk',
            'planUmsatz',
            'forecastUmsatz',
            'touristischePlanungRanking',
            'forecastUmsatzAtRisk',
            'forecastPaxPotenzial',
            'forecastUmsatzPotenzial',
            'zusaetzlichesPaxPotenzial',
            'zusaetzlichesUmsatzpotenzial',
          ];

          const response = await odataService.getReisetermin({
            filter: odataFilter,
            select: selectedFields,
            top: request.endRow - request.startRow,
            skip: request.startRow,
            expand: {
              reise: { select: ['reisekuerzel', 'country', 'region', 'prioritaet'] },
              pax: { select: ['min', 'max', 'ist', 'opt'] },
              metadata: {
                select: ['minPax', 'maxPax', 'durchfuehrbareMinPax', 'forecastPax', 'planPax', 'kalkPax'],
              },
              countryManagers: { select: ['countryManagerKuerzel'] },
              touristischePlanungErgebnis: {
                select: [
                  'id',
                  'istPax',
                  'personenFrei',
                  'vtg',
                  'istUmsatzControlling',
                  'durchschnittlicherVk',
                  'fvcStatus',
                  'fvcFlughafen',
                  'aufpreis',
                  'flugLimitation',
                  'landLimitation',
                  'istPaxUeberFcPax',
                  'istVkUeberSollVk',
                  'berechnetAm',
                  'forecastPax',
                ],
                orderBy: 'berechnetAm desc',
              },
            },
            orderBy: 'reise/reisekuerzel asc, startdatum asc',
          });

          return { success: true, rows: response.data };
        },
      };
    },

    getOdataFilter() {
      const f = this.filters;
      const range = f.startdatumRange;
      const filters = [
        { reiseterminSyncState: { in: BLANK_ANGELEGT_STATES } },
        { state: { ne: 'Verworfen' } },
        { isDeleted: { ne: true } },
      ];

      if (range && range[0]) {
        filters.push({ startdatum: { ge: new Date(range[0]) } });
      }
      if (range && range[1]) {
        const endDate = new Date(range[1]);
        endDate.setHours(23, 59, 59);
        filters.push({ startdatum: { le: endDate } });
      }

      // Zusatztermine-Toggle
      if (f.showZusatztermine) {
        filters.push({ istZusatztermin: true });
      }

      // Land, Reisekürzel, Status: einfaches "IN"-Filter
      if (f.country.length > 0) {
        filters.push({ reise: { country: { in: f.country } } });
      }
      if (f.reisekuerzel.length > 0) {
        filters.push({ reise: { reisekuerzel: { in: f.reisekuerzel } } });
      }
      if (f.state.length > 0) {
        filters.push({ state: { in: f.state } });
      }

      // Release-Datum als Halboffenes Intervall [start, end+1)
      if (f.releasedatum?.length === 2 && f.releasedatum[0]) {
        const startDate = new Date(f.releasedatum[0]);
        const endDate = new Date(f.releasedatum[1]);
        endDate.setDate(endDate.getDate() + 1);
        filters.push({ releasedatum: { ge: startDate, lt: endDate } });
      }

      // Country Manager – `any` ist OData-Syntax für "mindestens ein Element der
      // Collection erfüllt die Bedingung"
      if (f.countryManager.length > 0) {
        filters.push({
          countryManagers: {
            any: { countryManagerKuerzel: { in: f.countryManager } },
          },
        });
      }

      // Numerischer Vergleichsfilter: wird bereits im Child geparst
      if (f.forecastUmsatzAtRiskFilter) {
        filters.push({forecastUmsatzAtRisk: f.forecastUmsatzAtRiskFilter });
      }

      // Prioritäten sind bereits auf die numerischen Enum-Werte gemappt
      if (f.prioritiesValues?.length > 0) {
        filters.push({ reise: { prioritaet: { in: f.prioritiesValues } } });
      }

      return filters;
    },

    async updateSums() {
      const statusBar = this.gridApi?.getStatusPanel(STATUS_BAR_KEY);
      if (!statusBar) return;

      const aggregateFields = [
        'pax/ist with sum as istPax',
        'metadata/forecastPax with sum as fcPax',
        'ForecastPaxPotenzial with sum as fcPaxPotenzial',
        'umsatz with sum as istUmsatz',
        'forecastUmsatz with sum as fcUmsatz',
        'ForecastUmsatzPotenzial with sum as fcUmsatzPotenzial',
      ].join(', ');

      const filterExpression = this.buildFilterExpression();
      const url = `Reisetermin?$apply=filter(${filterExpression})/aggregate(${aggregateFields})`;

      try {
        const response = await apiService.get(url);
        const data = response.data?.result?.value?.[0];
        statusBar.setSums(data);
      } catch (e) {
        console.error('Fehler beim Laden der Gesamtsummen:', e);
      }
    },

    buildFilterExpression() {
      const query = buildQuery({ filter: this.getOdataFilter() });
      return query.replace(/^\?\$filter=/, '');
    },

    onReload() {
      this.gridApi.onFilterChanged();
    },

    async onPaxBearbeiten(event) {
      if (!event.valueChanged) return;

      const isPlan = event.colDef.field === 'metadata.planPax';
      const paramName = isPlan ? 'planPax' : 'forecastPax';
      const label = isPlan ? 'Plan Pax' : 'FC Pax';
      const newValue = event.data.metadata[paramName];

      try {
        const res = await apiService.patch('Reisetermin/updateForecastPlanPax', {
          id: event.data.id,
          [paramName]: newValue,
        });

        const result = res.data?.result?.value;
        const updated = Array.isArray(result) ? result.find(r => r.id === event.data.id) : result;

        if (updated) {
          // Der Merge erfolgt bewusst so: event.data als Basis, updated überschreibt,
          // und metadata separat mergen, damit im metadata-Objekt nichts verloren geht
          event.node.updateData({
            ...event.data,
            ...updated,
            metadata: { ...event.data.metadata, ...(updated.metadata || {}) },
          });
        }
        this.gridApi.refreshCells({ rowNodes: [event.node], force: true });
        this.updateSums();
        this.toast(`${label} erfolgreich bearbeitet.`);
      } catch (error) {
        // Rollback auf den alten Wert – UI soll nicht lügen
        event.data.metadata[paramName] = event.oldValue;
        this.gridApi.refreshCells({ rowNodes: [event.node], force: true });
        this.toast(`Fehler beim Bearbeiten von ${label}.`, { variant: 'danger' });
        console.error(`Fehler beim Speichern von ${label}:`, error);
      }
    },

    onRowSelected() {
      this.allSelectedRows = this.getAllSelectedRows();
    },

    getAllSelectedRows() {
      const rowsSelected = [];
      this.gridApi?.forEachNode(node => {
        if (node.selected) rowsSelected.push(node);
      });
      return rowsSelected;
    },

    openAufgabenModalWithData() {
      this.prefixedVerlinkungen = this.allSelectedRows.map((item, i) => ({
        id: i,
        typ: 'Reisetermin',
        artifactId: item.data.id,
        artifactText: item.data.reiseterminkuerzel,
      }));
      this.$bvModal.show('modalAufgabeEdit');
    },

    // Leere Shape für die Filter: Der Child-Filter emittet in `created()` sofort
    // den vollständigen Default-State. Diese Initialwerte stellen nur sicher,
    // dass `getOdataFilter()` bei einem theoretisch frühen Zugriff nicht auf
    // undefined-Properties läuft
    getEmptyFilters() {
      return {
        country: [],
        reisekuerzel: [],
        state: [],
        startdatumRange: [],
        releasedatum: [],
        countryManager: [],
        forecastUmsatzAtRisk: null,
        forecastUmsatzAtRiskFilter: null,
        prioritiesValues: [],
        showZusatztermine: false,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.ag-theme-alpine) {
  font-family: 'Poppins';
}

:deep(.ag-theme-alpine .ag-header-cell) {
  font-weight: 500;
  font-size: 14px;
}

:deep(.ag-cell),
:deep(.ag-header-cell) {
  padding: 0px 0.5rem;
}
</style>
